import React from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../routes";
import { EnterprisesAPI } from "../api/enterprises";

export default function PremiumFeatureGate({ title, description, children }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [tier, setTier] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    EnterprisesAPI.getMyEnterprise()
      .then((res) => {
        if (!mounted) return;
        setTier(String(res?.data?.data?.user_status || "").toLowerCase());
      })
      .catch(() => {
        if (!mounted) return;
        setError("Unable to verify your subscription tier.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tier && tier !== "free") {
    return children;
  }

  return (
    <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1.5, md: 2.5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          border: "1px solid #dbeafe",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LockIcon sx={{ color: "#94a3b8" }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Box>
          </Stack>

          {error ? <Alert severity="warning">{error}</Alert> : null}

          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
              Upgrade to use this feature
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This capability is available on paid plans and can be enabled when you’re ready to expand reporting, automation, vaulting, and maintenance workflows.
            </Typography>
          </Paper>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate(ROUTES.PRICING)}>
              View Plans
            </Button>
            <Button variant="outlined" onClick={() => navigate(ROUTES.ENTERPRISE)}>
              Back to Dashboard
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
